import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogIn, LogOut, User } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_61ca5ff4-bcc2-4cdc-8453-7407936ace18/artifacts/6nklllpo_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

/**
 * Simplified Header for Form Filing Pages
 * Shows only: TaxSetu branding | Back button | Login/Logout
 */
export default function SimplifiedHeader({ onBack, showBackButton = true }) {
  const navigate = useNavigate();
  
  // Check if user is logged in (from localStorage)
  const isLoggedIn = localStorage.getItem('practiq_user_session') !== null;
  const userEmail = isLoggedIn ? JSON.parse(localStorage.getItem('practiq_user_session') || '{}').email : null;
  
  const handleLogout = () => {
    localStorage.removeItem('practiq_user_session');
    localStorage.removeItem('practiq_tds_registration_session');
    window.location.reload();
  };
  
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };
  
  return (
    <>
      {/* Top Announcement Bar */}
      <div className="bg-gradient-to-r from-[#0a1628] via-[#0d1e36] to-[#0a1628] border-b border-white/5">
        <div className="w-full px-6 lg:px-12 py-2">
          <div className="flex items-center justify-center gap-2 text-xs">
            <span className="text-emerald-400 font-medium">EdTech Platform</span>
            <span className="text-gray-500">•</span>
            <span className="text-gray-400">India's No. 1 Tax Filing Simulation Tool</span>
            <span className="text-gray-500">•</span>
            <span className="text-amber-400">For Learning & Practice Only</span>
            <span className="text-gray-500">•</span>
            <span className="text-gray-400">Designed by CA</span>
          </div>
        </div>
      </div>
      
      {/* Main Header */}
      <header className="bg-[#0A0F1C]/95 backdrop-blur-md border-b border-white/5 sticky top-0 z-40">
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            
            {/* Left: Logo & Branding */}
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="TaxSetu AI" 
                className="h-10 w-auto"
              />
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold">
                  <span className="text-white">TaxSetu AI</span>
                  <span className="text-purple-400 ml-1">PractiQ</span>
                </h1>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">EdTech Learning Platform</p>
              </div>
            </div>
            
            {/* Center: Form 24Q Badge */}
            <div className="hidden md:flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-sm text-purple-300 font-medium">Form 24Q - TDS Return Filing</span>
            </div>
            
            {/* Right: Back & Login */}
            <div className="flex items-center gap-3">
              {/* Back Button */}
              {showBackButton && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all"
                  data-testid="back-button"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back</span>
                </button>
              )}
              
              {/* Login/Logout */}
              {isLoggedIn ? (
                <div className="flex items-center gap-2">
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg">
                    <User className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm text-gray-300 max-w-[120px] truncate">{userEmail}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg transition-all"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Logout</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/portal/login')}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-lg transition-all shadow-lg shadow-cyan-500/20"
                  data-testid="login-button"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Login</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
